import type { PortfolioBreakdownHierarchyNode, PortfolioBreakdownSubStrategy } from 'venn-components';

/**
 * Get list of strategies at a specific level from hierarchy
 * If a branch doesn't have children at the target level, use the deepest available level
 *
 * @param hierarchy - The hierarchy object from flattened portfolio
 * @param targetLevel - The level to extract strategies from (0 = asset class, 1 = first subdivision, etc.)
 * @param fallbackWithHigherLevel
 * @returns Array of strategies with their allocations and weights
 */
export function getStrategiesByLevel(
  hierarchy: PortfolioBreakdownHierarchyNode[],
  targetLevel: number | 'funds',
  topLevelAllocation: number,
  fallbackWithHigherLevel?: boolean,
): PortfolioBreakdownSubStrategy[] {
  const strategies: PortfolioBreakdownSubStrategy[] = [];

  // Process each asset class
  hierarchy.forEach((node) => {
    extractStrategiesAtLevel(
      node,
      [],
      [],
      undefined,
      0,
      targetLevel,
      strategies,
      topLevelAllocation,
      fallbackWithHigherLevel,
    );
  });
  return strategies;
}

/**
 * Recursively extract strategies at the target level
 */
function extractStrategiesAtLevel(
  node: PortfolioBreakdownHierarchyNode,
  parentPath: string[],
  parentWeights: number[],
  parentLabel: string | undefined,
  currentLevel: number,
  targetLevel: number | 'funds',
  strategies: PortfolioBreakdownSubStrategy[],
  topLevelAllocation: number,
  fallbackWithHigherLevel?: boolean,
): void {
  const path = [...parentPath, node.id];
  const weight = [...parentWeights, node.weight];
  const { id, label, allocation } = node;

  if (node.isFund) {
    if (targetLevel === 'funds') {
      strategies.push({
        id,
        label,
        path,
        allocation,
        weight,
        level: currentLevel,
        fundId: node.fundId,
        parentLabel,
      });
    } else {
      // Add an empty space if we need only strategies but have a fund
      strategies.push({
        id: '',
        label: '',
        path,
        allocation,
        weight,
        level: currentLevel,
      });
    }
  } else if (currentLevel === targetLevel) {
    // If we've reached the target level, add this is a strategy
    strategies.push({
      id,
      label,
      path,
      allocation,
      weight,
      level: currentLevel,
      parentLabel,
    });
  } else if (node.children?.length) {
    // recurse into children only if we haven’t reached target level
    node.children.forEach((childNode) => {
      extractStrategiesAtLevel(
        childNode,
        path,
        weight,
        label,
        currentLevel + 1,
        targetLevel,
        strategies,
        topLevelAllocation,
      );
    });

    const childrenAllocation = node.children.reduce((acc, child) => acc + child.allocation, 0);
    if (childrenAllocation < node.allocation) {
      // Normalize children weight with empty object, so sum of all children should be 1
      const childrenAllocation = node.children.reduce((acc, child) => acc + child.allocation, 0);
      const allocation = node.allocation - childrenAllocation;

      strategies.push({
        id: '',
        label: '',
        path,
        allocation,
        weight: [...weight, allocation / topLevelAllocation],
        level: currentLevel,
      });
    }
  } else if (fallbackWithHigherLevel) {
    // no children and haven't reached target level
    strategies.push({
      id,
      label,
      path,
      allocation,
      weight,
      level: currentLevel,
    });
  } else {
    strategies.push({
      // Empty object with allocation and weight to keep total data consistent
      id: '',
      label: '',
      path,
      allocation,
      weight,
      level: currentLevel,
    });
  }
}
